// Coordena a recuperação automática quando um `import()` dinâmico falha porque o
// arquivo do chunk não existe mais no servidor — o caso de uma aba que ficou
// aberta durante um deploy, que troca os arquivos com hash antigo por novos.
//
// Duas camadas chamam este módulo (o listener de `vite:preloadError` em main.tsx
// e o fallback do ErrorBoundary), então o guard precisa ser compartilhado: antes
// cada uma tinha a sua própria chave, e a do ErrorBoundary nunca era liberada.
const GUARD_KEY = 'nankin:chunk-reload-attempted-at';

// Janela em que uma segunda tentativa de reload é considerada loop. Passado esse
// tempo a aba volta a ter direito a uma tentativa — um deploy futuro na mesma
// sessão precisa se recuperar sozinho também.
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

// Marca se o reload foi disparado nesta execução da página. O Sentry usa isso
// para distinguir "deploy novo, já se resolveu sozinho" de "chunk realmente
// quebrado, o reload não adiantou".
let reloadDispatched = false;

export function shouldAttemptChunkReload(storage: Storage, now: number): boolean {
  const raw = storage.getItem(GUARD_KEY);
  if (raw !== null) {
    const last = Number(raw);
    if (Number.isFinite(last) && now - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
  }
  storage.setItem(GUARD_KEY, String(now));
  return true;
}

// Dispara o reload se o guard permitir. Retorna se recarregou.
export function requestChunkReload(): boolean {
  let allowed: boolean;
  try {
    allowed = shouldAttemptChunkReload(sessionStorage, Date.now());
  } catch {
    // sessionStorage indisponível (modo privado antigo, storage cheio): sem guard
    // confiável, não arriscamos um loop de reload.
    return false;
  }
  if (!allowed) return false;

  reloadDispatched = true;
  window.location.reload();
  return true;
}

export function wasChunkReloadDispatched(): boolean {
  return reloadDispatched;
}

// Só para testes.
export function resetChunkReloadStateForTests(): void {
  reloadDispatched = false;
}
